/** @jsxImportSource @emotion/react */

import { cx } from '@emotion/css';
import type { ReactElement, FC } from 'react';
import { useRef, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  /** Set is open, else collapsed */
  isOpen?: boolean;
  /** Unmount when closed */
  unmountOnExit?: boolean;
  /** Disable animation */
  animate?: boolean;
  /** Horizontal direction of collapse */
  horizontal?: boolean;
  /** Animation duration in ms */
  openDuration?: number;
  /** Collapse animation duration in ms */
  collapseDuration?: number;
}

const ODSCollapse: FC<Props> = ({
  isOpen = false,
  animate = true,
  unmountOnExit = true,
  openDuration = 175,
  collapseDuration = 125,
  horizontal,
  className,
  children,
  ...componentProps
}): ReactElement => {
  const [openSize, setSize] = useState<number | undefined>();
  const ref = useRef<HTMLDivElement>(null);
  const enterDuration = animate ? openDuration : 0;
  const exitDuration = animate ? collapseDuration : 0;
  const property = horizontal ? 'width' : 'height';
  const defaultStyle = {
    [property]: 0,
    transitionProperty: property,
    transitionTimingFunction: 'ease-out',
    transitionDuration: `${enterDuration}ms`,
  };
  const transitionStyles = {
    entering: { [property]: 0 },
    entered: { [property]: `${openSize}px` },
    exiting: {
      [property]: 0,
      transitionTimingFunction: 'ease-in',
      transitionDuration: `${exitDuration}ms`,
    },
    exited: { [property]: 0 },
  };

  useEffect(() => {
    const container = ref.current;
    if (container) {
      if (container[horizontal ? 'offsetWidth' : 'offsetHeight'] !== 0) {
        const nodes = Array.from(container.children);
        let minP = Infinity;
        let maxP = -Infinity;
        const point1 = horizontal ? 'left' : 'top';
        const point2 = horizontal ? 'right' : 'bottom';

        nodes.forEach((node) => {
          const computedStyle = window.getComputedStyle(node);
          const position = computedStyle.getPropertyValue('position');
          if (position === 'absolute' || position === 'fixed') {
            return;
          }
          const rect = node.getBoundingClientRect();
          minP = Math.min(minP, rect[point1]);
          maxP = Math.max(maxP, rect[point2]);
        });

        const maxSize = maxP - minP;
        setSize(maxSize);
        return;
      }

      setSize(horizontal ? container.scrollWidth : container.scrollHeight);
    }
  }, [setSize, horizontal, children]);

  return (
    <CSSTransition
      in={isOpen}
      timeout={{
        enter: 0,
        exit: isOpen ? enterDuration : exitDuration,
      }}
      classNames="ods-collapse"
      nodeRef={ref}
      unmountOnExit={unmountOnExit}
    >
      {(state) => (
        <div
          className={cx('flex-shrink-0 overflow-hidden', className)}
          style={{
            ...defaultStyle,
            ...transitionStyles[state],
          }}
          ref={ref}
          aria-expanded={isOpen}
          {...componentProps}
        >
          {children}
        </div>
      )}
    </CSSTransition>
  );
};

export default ODSCollapse;
